import { useEffect, useState } from 'react';
import RouterProvider from '@core/providers/RouterProvider';
import { ErrorPage } from '@core/pages/ErrorPage/ErrorPage';
import { TUiConfig } from '@core/shared/types';
import useUserStore from '@core/stores/useUserStore';
import { logger } from '@shared/logger';
import { removeCookie } from 'tiny-cookie';
import { MASTER_UI, TENANT_COOKIE_STORAGE_KEY } from '../../tenantConfig';
import { getTenantInfo } from '@shared/getTenantInfo';
import { noPermissionRedirect } from '@shared/utils';

const SCOPE_NAME = 'UiProvider';

/**
 * The UiProvider component is responsible for loading the UI configuration for the current tenant
 * and rendering the corresponding router or error page. It manages loading state, error handling,
 * and configures the RouterProvider once the UI configuration is successfully loaded.
 *
 * @returns The rendered UI, either the RouterProvider or ErrorPage.
 */
export const UiProvider = () => {
    const [uiConfig, setUiConfig] = useState<TUiConfig | null>(null);
    const [error, setError] = useState(false);
    const token = useUserStore((state) => state.token); // Access user token from the store
    const { uiKey, uiApiTenantKey } = getTenantInfo();

    useEffect(() => {
        /**
         * Loads the UI configuration asynchronously based on the uiKey.
         * If the UI configuration is successfully loaded, it updates the state to reflect the config.
         * In case of an error, it logs the error and clears the tenant key from session storage.
         */
        const loadUiConfig = async () => {
            if (!uiKey) return; // Return early if no UI key is available

            try {
                logger.log(SCOPE_NAME, 1, 'Loading UI config:', uiKey);
                const uiConfigImport = await import(`@ui/${uiKey}/index.ts`); // Dynamically import the UI config
                const uiConfig = uiConfigImport.default as TUiConfig;

                // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                // if its the master tenant, we need to override the apiTenantKey for each route
                // only relation to the master tenant from the core side
                // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

                if (uiKey === MASTER_UI) {
                    logger.log(
                        SCOPE_NAME,
                        1,
                        'Its the master tenant, setting apiTenantKey for each route:',
                        uiApiTenantKey,
                    );
                    uiConfig.routeConfig.forEach((route) => {
                        route.apiTenantKey = uiApiTenantKey as string; // Override the API tenant key for each route if it is _master
                    });
                }
                // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

                setUiConfig(uiConfig); // Set the UI configuration
            } catch (error) {
                console.error('Failed to load UI config:', error); // Log error if loading fails
                removeCookie(TENANT_COOKIE_STORAGE_KEY); // Clear tenant key from cookie storage
                setError(true); // Set error state to true
            }
        };

        loadUiConfig();
    }, [uiApiTenantKey, uiKey]); // The effect runs when the uiKey changes (on mount or when uiKey is updated)

    if (!uiKey && token) {
        // captureMessage removed: User has no EN GenAi permissions
        removeCookie(TENANT_COOKIE_STORAGE_KEY); // Clear tenant key from cookie storage
        noPermissionRedirect();
    }

    if (error) {
        return <ErrorPage />; // Render ErrorPage if there's an error loading the UI config
    }

    if (!uiConfig) {
        return; // Render nothing until the UI configuration is successfully loaded
    }

    // Render RouterProvider once the UI configuration is loaded
    return <RouterProvider uiConfig={uiConfig} />;
};
